import { Box, styled } from '@mui/material';
import { LwButton } from 'redesign';
import { FormProvider, useForm } from 'react-hook-form';
import { CompanyOrt, EmploymentType } from '@types';
import { useCreateCompanyOrt } from 'hq/hooks/queryHooks/companies/use-create-company-ort';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useAlert } from 'shared/components/alerts';
import { useDialog } from 'ogp/providers/dialog-provider';
import { IconApproved, IconCancel } from 'assets/img';
import {
  checkPeriodsOverlap,
  DAYS_OF_WEEK,
  HOLIDAY_OPTIONS,
  holidayData,
  OrtData,
  ortDayData,
  timeToMinutes,
  transformFormDataToOrt,
} from './utils';
import { PeriodTableSection } from './ort-period-table';
import { PeriodGroup } from './ort-period-group';
import { FormHeaderSection } from './company-ort-form-header';

export type OrtPeriod = {
  startTime: string;
  endTime: string;
  surcharge: number;
  surchargeType: 'factor';
};

type CompanyOrtFormProps = {
  onClose: () => void;
  ort: CompanyOrt | undefined;
  companyId: string;
};

export type CompanyOrtFormData = {
  utcStartDate: DateTime;
  days: OrtData;
  holidays: HolidayData;
  name: string;
  employmentType: EmploymentType;
  holidaySelector?: string;
  ortId: string;
  surchargeType: 'factor';
  companyId: string;
};

type HolidayData = {
  [holidayName: string]: OrtPeriod[];
};

type EditingState = {
  type: 'day' | 'holiday';
  key: string;
  isNew: boolean;
};
type ExpandedSections = {
  workdays: boolean;
  holidays: boolean;
};

export const CompanyOrtForm = ({ onClose, ort, companyId }: CompanyOrtFormProps) => {
  const formMethods = useForm<CompanyOrtFormData>({
    defaultValues: {
      name: ort?.name || '',
      employmentType: ort ? ort.employmentType : EmploymentType.FREELANCER,
      days: ortDayData(ort),
      holidays: ort ? holidayData(ort) : {},
      utcStartDate: ort
        ? DateTime.fromISO(ort.utcStartDate).toISODate()
        : DateTime.now().toISODate(),
      ortId: ort ? ort.id : '',
      surchargeType: 'factor',
      companyId: companyId,
    },
  });
  const { control, watch, setValue, handleSubmit } = formMethods;
  const { alertSuccess, alertError } = useAlert();
  const { openDialog, closeDialog } = useDialog();

  const ortData = watch('days');
  const holidays = watch('holidays');
  const globalSurchargeType = watch('surchargeType');

  const emptyTimePeriod = {
    startTime: '',
    endTime: '',
    surcharge: 0,
    surchargeType: globalSurchargeType,
  };
  const [editingState, setEditingState] = useState<EditingState | null>(null);
  const [tempPeriod, setTempPeriod] = useState<OrtPeriod>(emptyTimePeriod);
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>({
    workdays: true,
    holidays: false,
  });

  const isEditMode = Boolean(ort);
  const title = `${isEditMode ? 'Duplicate' : 'Create'} ORT`;

  const createCompanyOrt = useCreateCompanyOrt(companyId);

  useEffect(() => {
    const updatedDays = { ...ortData };
    Object.keys(updatedDays).forEach((day) => {
      updatedDays[day] = updatedDays[day].map((period) => ({
        ...period,
        surchargeType: globalSurchargeType,
      }));
    });

    const updatedHolidays = { ...holidays };
    Object.keys(updatedHolidays).forEach((holiday) => {
      updatedHolidays[holiday] = updatedHolidays[holiday].map((period) => ({
        ...period,
        surchargeType: globalSurchargeType,
      }));
    });

    setValue('days', updatedDays, { shouldDirty: true });
    setValue('holidays', updatedHolidays, { shouldDirty: true });
  }, [globalSurchargeType]);

  const onSubmit = (formData: CompanyOrtFormData) => {
    if (editingState) {
      alertError(new Error('Rond eerst het toevoegen of bewerken van een periode af.'));
      return;
    }
    openDialog(
      {
        title: 'ORT Toevoegen',
        content: `Wil je deze ORT aanmaken? De ORT gaat in op de startdatum en wordt niet met terugwerkende kracht toegepast.`,
        secondaryButton: (props) => (
          <LwButton iconColorMode="fill" color="secondary" startIcon={<IconCancel />} {...props}>
            Annuleer
          </LwButton>
        ),
        primaryButton: (props) => (
          <LwButton color="primary" startIcon={<IconApproved />} {...props}>
            Bevestigen
          </LwButton>
        ),
      },
      () => {
        const ortPayload = transformFormDataToOrt(formData, companyId);
        createCompanyOrt.mutate(ortPayload, {
          onSuccess: () => {
            alertSuccess('ORT succesvol aangemaakt');
            closeDialog();
            onClose();
          },
          onError: (e) => alertError(e),
        });
      }
    );
  };

  const isLoading = createCompanyOrt.isLoading;

  const handleAddPeriod = (type: 'day' | 'holiday', key: string) => {
    setEditingState({ type, key, isNew: true });
    setTempPeriod(emptyTimePeriod);
  };

  const handleSavePeriod = (type: 'day' | 'holiday', key: string) => {
    if (!tempPeriod.startTime || !tempPeriod.endTime) {
      alertError(new Error('Voer zowel start- als eindtijd in'));
      return;
    }

    if (timeToMinutes(tempPeriod.startTime) >= timeToMinutes(tempPeriod.endTime)) {
      alertError(new Error('Starttijd moet voor de eindtijd liggen'));
      return;
    }

    const periodsToCheck = type === 'day' ? ortData[key] || [] : holidays[key] || [];

    for (let i = 0; i < periodsToCheck.length; i++) {
      const existingPeriod = periodsToCheck[i];
      if (
        checkPeriodsOverlap(
          tempPeriod.startTime,
          tempPeriod.endTime,
          existingPeriod.startTime,
          existingPeriod.endTime
        )
      ) {
        alertError(
          new Error(
            `Deze periode overlapt met een bestaande periode (${existingPeriod.startTime} - ${existingPeriod.endTime})`
          )
        );
        return;
      }
    }

    if (type === 'day') {
      const dayPeriods = ortData[key] || [];
      setValue(`days.${key}`, [...dayPeriods, tempPeriod], { shouldDirty: true });
    } else if (type === 'holiday') {
      const holidayPeriods = holidays[key] || [];
      setValue(`holidays.${key}`, [...holidayPeriods, tempPeriod], { shouldDirty: true });
    }

    setEditingState(null);
    setTempPeriod(emptyTimePeriod);
  };

  const handleCancelEdit = () => {
    setEditingState(null);
    setTempPeriod(emptyTimePeriod);
  };

  const handleDeletePeriod = (type: 'day' | 'holiday', key: string, index: number) => {
    if (type === 'day') {
      const dayPeriods = ortData[key] || [];
      const updatedPeriods = dayPeriods.filter((_, i) => i !== index);
      setValue(`days.${key}`, updatedPeriods, { shouldDirty: true });
    } else if (type === 'holiday') {
      const holidayPeriods = holidays[key] || [];
      const updatedPeriods = holidayPeriods.filter((_, i) => i !== index);
      setValue(`holidays.${key}`, updatedPeriods, { shouldDirty: true });
    }
  };

  return (
    <CompanyOrtFormWrapper>
      <FormProvider {...formMethods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          <FormHeaderSection
            title={title}
            subtitle={`Configureer het ORT-percentage voor verschillende tijdsperioden per dag.${
              ort ? ` (${ort.name})` : ''
            }`}
            control={control}
            minDate={DateTime.now().toISODate()}
            onClose={onClose}
          />
          <ScrollableContent>
            <PeriodTableSection
              title="Workdays"
              isExpanded={expandedSections.workdays}
              onToggle={() =>
                setExpandedSections((prev) => ({ ...prev, workdays: !prev.workdays }))
              }
            >
              {DAYS_OF_WEEK.map((day) => {
                const dayPeriods = ortData[day] || [];
                const isEditingThisDay = editingState?.type === 'day' && editingState?.key === day;

                return (
                  <PeriodGroup
                    key={day}
                    label={day}
                    periods={dayPeriods}
                    isEditing={isEditingThisDay}
                    tempPeriod={tempPeriod}
                    onAdd={() => handleAddPeriod('day', day)}
                    onDelete={(index) => handleDeletePeriod('day', day, index)}
                    onSave={() => handleSavePeriod('day', day)}
                    onCancel={handleCancelEdit}
                    onTempPeriodChange={(updates) => {
                      setTempPeriod((prev) => {
                        const merged = { ...prev, ...updates };
                        return {
                          startTime: merged.startTime,
                          endTime: merged.endTime,
                          surcharge: merged.surcharge ?? 0,
                          surchargeType: merged.surchargeType,
                        };
                      });
                    }}
                  />
                );
              })}
            </PeriodTableSection>

            <PeriodTableSection
              title="Holidays"
              isExpanded={expandedSections.holidays}
              onToggle={() =>
                setExpandedSections((prev) => ({ ...prev, holidays: !prev.holidays }))
              }
            >
              {HOLIDAY_OPTIONS.map((holidayName) => {
                const holidayPeriods = holidays[holidayName] || [];
                const isEditingThisHoliday =
                  editingState?.type === 'holiday' && editingState?.key === holidayName;

                return (
                  <PeriodGroup
                    key={holidayName}
                    label={holidayName}
                    periods={holidayPeriods}
                    isEditing={isEditingThisHoliday}
                    tempPeriod={tempPeriod}
                    onAdd={() => handleAddPeriod('holiday', holidayName)}
                    onDelete={(index) => handleDeletePeriod('holiday', holidayName, index)}
                    onSave={() => handleSavePeriod('holiday', holidayName)}
                    onCancel={handleCancelEdit}
                    onTempPeriodChange={(updates) => {
                      setTempPeriod((prev) => {
                        const merged = { ...prev, ...updates };
                        return {
                          startTime: merged.startTime,
                          endTime: merged.endTime,
                          surcharge: merged.surcharge ?? 0,
                          surchargeType: merged.surchargeType,
                        };
                      });
                    }}
                  />
                );
              })}
            </PeriodTableSection>
          </ScrollableContent>

          <ActionBox>
            <LwButton type="submit" color="primary" disabled={isLoading}>
              Maak aan
            </LwButton>
          </ActionBox>
        </form>
      </FormProvider>
    </CompanyOrtFormWrapper>
  );
};

const CompanyOrtFormWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  paddingRight: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const ActionBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  paddingTop: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
}));
